// Email-campaign wizard assets: predefined templates + scoped CSS.

// Trending one-liner goals shown as chips in the "Generate with AI" tab.
// Clicking one fills the prompt input below; user can edit before generating.
export const AI_PROMPT_TRENDS = [
  'Announce a new product feature to our leads',
  'Follow up after NFC tap at a networking event',
  'Invite leads to an exclusive webinar',
  'Re-engage cold leads with a special offer',
  'Send a monthly company newsletter',
  'Promote a limited-time partnership offer',
];

// Merge tags shown as click-to-insert chips above the email body editor.
// These resolve at send time using the recipient's lead row (see SendCampaignJob).
export const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First name' },
  { tag: '{{company}}',    label: 'Company' },
  { tag: '{{ai_score}}',   label: 'AI Score' },
];

export const EMAIL_TEMPLATES = [
  {
    id: 'product-launch',
    name: 'Product Launch',
    tag: 'Announcement',
    accent: '#0891b2',
    desc: 'Bold header with feature highlights and CTA.',
    subject: '🚀 Something new from {{company_name}}',
    body:
      '<p>Hi {{first_name}},</p>' +
      '<p>We just shipped something we are genuinely excited about — and we built it with people like you in mind.</p>' +
      '<p><strong>What you get:</strong></p>' +
      '<ul>' +
      '<li>Faster, simpler workflow that saves you hours every week</li>' +
      '<li>Built-in analytics so you always know what is working</li>' +
      '<li>Designed for teams of every size</li>' +
      '</ul>' +
      '<p>Take a look — we think you will like it.</p>',
    cta_text: 'See What is New',
  },
  {
    id: 'newsletter',
    name: 'Monthly Newsletter',
    tag: 'Newsletter',
    accent: '#7c3aed',
    desc: 'Clean layout for monthly updates and roundups.',
    subject: '{{company_name}} — this month in review',
    body:
      '<p>Hi {{first_name}},</p>' +
      '<p>Here is what happened this month at <strong>{{company_name}}</strong>:</p>' +
      '<p><strong>Highlights</strong></p>' +
      '<ul>' +
      '<li>New features launched</li>' +
      '<li>Customer story of the month</li>' +
      '<li>What is coming next</li>' +
      '</ul>' +
      '<p>Thanks for being part of the journey.</p>',
    cta_text: 'Read the Full Update',
  },
  {
    id: 'follow-up',
    name: 'NFC Follow-up',
    tag: 'Outreach',
    accent: '#059669',
    desc: 'Personal note after a tap or networking event.',
    subject: 'Great meeting you',
    body:
      '<p>Hi {{first_name}},</p>' +
      '<p>It was great to meet you. I wanted to follow up while the conversation is still fresh — based on what you shared, I think there is real opportunity for us to work together.</p>' +
      '<p>I have put together a short overview of how teams use our platform to capture more leads and convert them faster.</p>' +
      '<p>Would you have 15 minutes this week for a quick call?</p>',
    cta_text: 'Book 15 Minutes',
  },
  {
    id: 'invite',
    name: 'Webinar Invite',
    tag: 'Event',
    accent: '#d97706',
    desc: 'Event invitation with date and signup CTA.',
    subject: 'You are invited — exclusive session',
    body:
      '<p>Hi {{first_name}},</p>' +
      '<p>We are hosting an exclusive session for a small group of professionals — and we would like you to be part of it.</p>' +
      '<p><strong>What to expect:</strong></p>' +
      '<ul>' +
      '<li>Live walkthrough by our product team</li>' +
      '<li>Real customer Q&A — no scripted demos</li>' +
      '<li>First look at features rolling out next quarter</li>' +
      '</ul>' +
      '<p>Seats are limited. Save yours below.</p>',
    cta_text: 'Reserve My Spot',
  },
  {
    id: 're-engagement',
    name: 'Re-engagement',
    tag: 'Win-back',
    accent: '#dc2626',
    desc: 'Bring back cold leads with a warm, personal note.',
    subject: 'We miss you — here is what is new',
    body:
      '<p>Hi {{first_name}},</p>' +
      '<p>It has been a while since we last connected, and we wanted to reach out personally.</p>' +
      '<p>A lot has changed at <strong>{{company_name}}</strong> since you were last here — we have shipped new features, refined the experience, and built things based on feedback from people like you.</p>' +
      '<p><strong>Worth a fresh look:</strong></p>' +
      '<ul>' +
      '<li>New tools that save you real time</li>' +
      '<li>A simpler, faster experience end-to-end</li>' +
      '<li>Better support and onboarding</li>' +
      '</ul>' +
      '<p>If now is a better moment to take another look, we would love to have you back.</p>',
    cta_text: 'Take Another Look',
  },
  {
    id: 'blank',
    name: 'Blank',
    tag: 'Start fresh',
    accent: '#64748b',
    desc: 'Empty canvas — write your own subject and body from scratch.',
    subject: '',
    body: '',
    cta_text: '',
  },
];

// Mini visual preview rendered inside each template card on Step 3.
// Blank template gets a distinct "empty canvas" look so it does not look
// like just another colored card.
export function templatePreview(template) {
  if (template.id === 'blank') {
    return (
      <div
        style={{
          background: '#f8fafc',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed rgba(100,116,139,.3)',
          borderRadius: 0,
        }}
      >
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: 28, fontWeight: 200, lineHeight: 1, marginBottom: 6 }}>+</div>
          <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em' }}>BLANK CANVAS</div>
        </div>
      </div>
    );
  }

  const accent = template.accent;
  return (
    <div
      style={{
        background: `linear-gradient(160deg, ${accent}, ${accent}cc)`,
        height: '100%',
        padding: '14px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(255,255,255,.22)' }} />
      <div style={{ height: 8, width: '70%', background: 'rgba(255,255,255,.92)', borderRadius: 3 }} />
      <div style={{ height: 5, width: '90%', background: 'rgba(255,255,255,.5)', borderRadius: 3 }} />
      <div style={{ height: 5, width: '80%', background: 'rgba(255,255,255,.5)', borderRadius: 3 }} />
      <div style={{ height: 5, width: '60%', background: 'rgba(255,255,255,.5)', borderRadius: 3 }} />
      <div
        style={{
          marginTop: 'auto',
          height: 22,
          width: 84,
          background: 'rgba(255,255,255,.95)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ height: 4, width: 56, background: accent, borderRadius: 2 }} />
      </div>
    </div>
  );
}

export const CAMPAIGN_CSS = `
@keyframes ec-stepIn{from{opacity:0;transform:translateX(18px)}to{opacity:1;transform:translateX(0)}}
@keyframes ec-tplPop{0%{opacity:0;transform:scale(.94) translateY(8px)}100%{opacity:1;transform:scale(1) translateY(0)}}
.ec-step{animation:ec-stepIn .32s cubic-bezier(.16,1,.3,1) both}
.ec-overlay{position:fixed;inset:0;z-index:400;display:flex;align-items:stretch;background:rgba(15,23,42,.45);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.ec-drawer{width:100%;max-width:820px;margin-left:auto;background:rgba(255,255,255,.97);backdrop-filter:blur(40px);display:flex;flex-direction:column;height:100vh;overflow:hidden;box-shadow:0 0 80px rgba(0,0,0,.22)}
.ec-hd{padding:22px 32px 0;border-bottom:1px solid rgba(0,0,0,.07);flex-shrink:0}
.ec-steps{display:flex;gap:0;margin-top:18px}
.ec-step-item{display:flex;align-items:center;gap:9px;padding:11px 0;position:relative;flex:1;cursor:default}
.ec-step-item::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2.5px;background:transparent;border-radius:2px;transition:background .3s}
.ec-step-item.done::after{background:#059669}
.ec-step-item.act::after{background:#0891b2}
.ec-step-num{width:26px;height:26px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:760;flex-shrink:0;transition:all .22s}
.ec-step-item.done .ec-step-num{background:#059669;color:#fff;box-shadow:0 2px 8px rgba(5,150,105,.3)}
.ec-step-item.act .ec-step-num{background:#0891b2;color:#fff;box-shadow:0 2px 8px rgba(8,145,178,.3)}
.ec-step-item.pend .ec-step-num{background:rgba(0,0,0,.07);color:#94a3b8}
.ec-step-lbl{font-size:12px;font-weight:600;color:#475569;transition:color .2s}
.ec-step-item.act .ec-step-lbl{color:#0891b2}
.ec-step-item.done .ec-step-lbl{color:#059669}
.ec-step-con{width:24px;height:1.5px;background:rgba(0,0,0,.1);flex-shrink:0}
.ec-body{flex:1;overflow-y:auto;padding:28px 32px}
.ec-body::-webkit-scrollbar{width:4px}
.ec-body::-webkit-scrollbar-track{background:transparent}
.ec-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.12);border-radius:4px}
.ec-ft{padding:18px 32px;border-top:1px solid rgba(0,0,0,.07);display:flex;align-items:center;gap:10px;background:rgba(255,255,255,.95);flex-shrink:0}
.ec-seg-opt{display:flex;align-items:flex-start;gap:14px;padding:16px 18px;border-radius:14px;cursor:pointer;border:2px solid rgba(0,0,0,.07);background:rgba(255,255,255,.7);transition:all .18s;margin-bottom:10px}
.ec-seg-opt:hover{border-color:rgba(8,145,178,.3);background:rgba(8,145,178,.04)}
.ec-seg-opt.sel{border-color:#0891b2;background:rgba(8,145,178,.05);box-shadow:0 0 0 4px rgba(8,145,178,.08)}
.ec-seg-ico{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
.ec-radio{width:20px;height:20px;border-radius:50%;border:2px solid rgba(0,0,0,.18);background:transparent;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .15s}
.ec-radio.on{border-color:#0891b2;background:#0891b2}
.ec-radio.on::after{content:'';width:8px;height:8px;border-radius:50%;background:#fff}
.ec-pick-row{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:11px;cursor:pointer;border:1.5px solid rgba(0,0,0,.07);background:rgba(255,255,255,.65);transition:all .15s;margin-bottom:7px}
.ec-pick-row:hover{border-color:rgba(8,145,178,.3);background:rgba(8,145,178,.04)}
.ec-pick-row.sel{border-color:#0891b2;background:rgba(8,145,178,.06);box-shadow:0 0 0 3px rgba(8,145,178,.1)}
.ec-pick-cb{width:18px;height:18px;border-radius:5px;border:2px solid rgba(0,0,0,.18);background:transparent;display:flex;align-items:center;justify-content:center;font-size:10px;color:#fff;font-weight:800;flex-shrink:0;transition:all .15s}
.ec-pick-cb.on{border-color:#0891b2;background:#0891b2}
.ec-tpl-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:20px}
@media(min-width:720px){.ec-tpl-grid{grid-template-columns:repeat(3,1fr)}}
.ec-tpl{position:relative;border-radius:14px;border:2px solid rgba(0,0,0,.07);overflow:hidden;cursor:pointer;transition:all .2s;background:#fff;animation:ec-tplPop .3s cubic-bezier(.16,1,.3,1) both}
.ec-tpl:hover{border-color:rgba(8,145,178,.4);box-shadow:0 8px 28px rgba(0,0,0,.1);transform:translateY(-2px)}
.ec-tpl.sel{border-color:#0891b2;box-shadow:0 0 0 4px rgba(8,145,178,.12),0 8px 28px rgba(0,0,0,.1)}
.ec-tpl-preview{height:130px;position:relative;overflow:hidden}
.ec-tpl-foot{padding:10px 12px;border-top:1px solid rgba(0,0,0,.06)}
.ec-tpl-name{font-size:12px;font-weight:680;color:#0f172a}
.ec-tpl-tag{font-size:9.5px;color:#64748b;margin-top:2px}
.ec-tpl-check{position:absolute;top:8px;right:8px;width:22px;height:22px;border-radius:50%;background:#0891b2;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;font-weight:800;box-shadow:0 2px 8px rgba(8,145,178,.4)}
.ec-email-preview{border-radius:13px;border:1px solid #e2e8f0;overflow:hidden;background:#fff;box-shadow:0 4px 16px rgba(0,0,0,.06)}
.ec-email-chrome{display:flex;gap:5px;padding:8px 12px;background:#f8fafc;border-bottom:1px solid #e2e8f0;align-items:center}
.ec-email-dot{width:8px;height:8px;border-radius:50%}
.ec-summary-row{display:flex;justify-content:space-between;align-items:center;padding:9px 0;border-bottom:1px solid rgba(0,0,0,.05)}
.ec-summary-row:last-child{border-bottom:none}
.ec-sched-opt{display:flex;flex-direction:column;align-items:center;gap:7px;padding:18px 12px;border-radius:14px;cursor:pointer;border:2px solid rgba(0,0,0,.07);background:rgba(255,255,255,.7);transition:all .18s;flex:1}
.ec-sched-opt:hover{border-color:rgba(8,145,178,.3);background:rgba(8,145,178,.04)}
.ec-sched-opt.sel{border-color:#0891b2;background:rgba(8,145,178,.05);box-shadow:0 0 0 4px rgba(8,145,178,.08)}
.ec-status-pill{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:660;padding:3px 9px;border-radius:20px;text-transform:capitalize;letter-spacing:.02em}
.ec-status-pill.draft{background:rgba(100,116,139,.1);color:#475569;border:1px solid rgba(100,116,139,.22)}
.ec-status-pill.scheduled{background:rgba(217,119,6,.09);color:#b45309;border:1px solid rgba(217,119,6,.22)}
.ec-status-pill.sent{background:rgba(5,150,105,.09);color:#047857;border:1px solid rgba(5,150,105,.22)}
.ec-status-pill.cancelled{background:rgba(100,116,139,.08);color:#94a3b8;border:1px solid rgba(100,116,139,.18);text-decoration:line-through}
.ec-status-pill.failed{background:rgba(220,38,38,.08);color:#b91c1c;border:1px solid rgba(220,38,38,.22)}
.ec-content-tabs{display:flex;gap:6px;padding:5px;background:rgba(0,0,0,.04);border-radius:13px;margin-bottom:18px}
.ec-content-tab{flex:1;padding:11px 14px;border-radius:10px;border:none;background:transparent;font-size:12.5px;font-weight:660;color:#64748b;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:7px;transition:all .18s}
.ec-content-tab:hover{color:#0f172a}
.ec-content-tab.act{background:#fff;color:#0f172a;box-shadow:0 1px 3px rgba(0,0,0,.06),0 4px 12px rgba(0,0,0,.04)}
.ec-ai-card{padding:18px 20px;border-radius:14px;border:1px solid rgba(8,145,178,.18);background:linear-gradient(135deg,rgba(8,145,178,.04),rgba(124,58,237,.04));margin-bottom:18px}
.ec-ai-chips{display:flex;flex-wrap:wrap;gap:8px;margin:14px 0}
.ec-ai-chip{padding:7px 14px;border-radius:20px;border:1px solid rgba(0,0,0,.1);background:rgba(255,255,255,.85);font-size:11.5px;font-weight:560;color:#475569;cursor:pointer;transition:all .15s}
.ec-ai-chip:hover{border-color:rgba(8,145,178,.4);color:#0f172a}
.ec-ai-chip.sel{border-color:#0891b2;color:#0891b2;background:rgba(8,145,178,.08)}
.ec-ai-row{display:flex;gap:8px}
.ec-ai-row .inp{flex:1}
.ec-merge-bar{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.ec-merge-chips{display:flex;gap:6px;flex-wrap:wrap}
.ec-merge-chip{padding:4px 10px;border-radius:14px;border:1px solid rgba(0,0,0,.08);background:rgba(255,255,255,.8);font-size:10.5px;font-weight:600;color:#475569;cursor:pointer;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;transition:all .15s}
.ec-merge-chip:hover{border-color:rgba(8,145,178,.4);color:#0891b2;background:rgba(8,145,178,.06)}
@keyframes ec-spin{to{transform:rotate(360deg)}}
.ec-spin{display:inline-block;width:14px;height:14px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:ec-spin .7s linear infinite;flex-shrink:0;vertical-align:-2px}
`;
